import { format } from "date-fns";
import { BRIEFING_STATUS_OPTIONS } from "./briefingStatus";

export type TimelineVisualState =
  | "pending"
  | "completed"
  | "current"
  | "reopened";

export interface StatusAuditLike {
  action?: string;
  createdAt?: string | Date;
  date?: string | Date;
  data?: {
    Status?: string;
    status?: string;
    Justificativa?: string;
    justification?: string;
  };
}

export interface TimelineStep {
  statusValue: string;
  statusLabel: string;
  durationSeconds: number;
  durationLabel: string;
  visitCount: number;
  isRevisited: boolean;
  visualState: TimelineVisualState;
  justification?: string;
}

export interface StatusHistoryRow {
  statusValue: string;
  statusLabel: string;
  enteredAt: Date;
  exitedAt: Date | null;
  enteredAtLabel: string;
  exitedAtLabel: string;
  durationSeconds: number;
  durationLabel: string;
  occurrence: number;
  occurrenceLabel: string;
  isReturn: boolean;
  justification?: string;
}

export interface ProjectStatusTimelineModel {
  steps: TimelineStep[];
  history: StatusHistoryRow[];
  totalDurationSeconds: number;
  totalDurationLabel: string;
  createdAtLabel: string;
}

/** Happy-path track shown on the project page (matches the product timeline). */
export const TIMELINE_TRACK_VALUES = [
  "BUDGET",
  "ON_APROVAL",
  "APPROVED",
  "FINISHED",
  "PRE_CLOSING",
  "CLEARED_OPERATIONS",
  "WAITING_CLIENT",
  "FINANCIAL",
  "LEGAL_REPORTING",
  "BILLED",
  "BILLED_PENDING_AUDIT",
  "AUDITED",
] as const;

const STATUS_ALIASES: Record<string, string> = {
  ON_APPROVAL: "ON_APROVAL",
  WAITING_FOR_CUSTOMER: "WAITING_CLIENT",
  DUPLICITY: "DUPLICATE",
  "Pré-Fechamento": "PRE_CLOSING",
  "Pré-fechamento": "PRE_CLOSING",
  Negociação: "ON_APROVAL",
  Financeiro: "FINANCIAL",
  Faturamento: "FINANCIAL",
};

/** Status finais: Concluído e sem contador enquanto o projeto permanece neles. */
export const TERMINAL_TIMELINE_STATUS_VALUES = new Set([
  "AUDITED",
  "CANCELED",
  "DECLINED",
  "DUPLICATE",
  "LOST",
]);

export function isTerminalTimelineStatus(
  statusValue: string | undefined | null
): boolean {
  return Boolean(
    statusValue && TERMINAL_TIMELINE_STATUS_VALUES.has(statusValue)
  );
}

function padTime(value: number): string {
  return String(value).padStart(2, "0");
}

export function formatDuration(seconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const h = hours % 24;
  const m = minutes % 60;

  if (days > 0) return `${days}d ${h}h ${m}m`;
  if (hours > 0) return `${hours}h ${m}m`;
  return `${m}m`;
}

export function formatHistoryDateTime(date: Date): string {
  return format(date, "dd/MM/yy · HH'h'mm");
}

export function formatCreatedAtShort(date: Date): string {
  return format(date, "dd/MM/yy");
}

export function formatOccurrence(occurrence: number): string {
  return `${occurrence}ª vez`;
}

export function resolveStatus(raw: string | undefined | null): {
  value: string;
  label: string;
} | null {
  if (!raw) return null;
  const aliased = STATUS_ALIASES[raw] ?? raw;
  const byValue = BRIEFING_STATUS_OPTIONS.find((o) => o.value === aliased);
  if (byValue) return { value: byValue.value, label: byValue.text };
  const byLabel = BRIEFING_STATUS_OPTIONS.find(
    (o) => o.text.toLowerCase() === aliased.toLowerCase()
  );
  if (byLabel) return { value: byLabel.value, label: byLabel.text };
  return { value: aliased, label: raw };
}

function getAuditTimestamp(audit: StatusAuditLike): number {
  const raw = audit.createdAt ?? audit.date;
  const time = raw ? new Date(raw).getTime() : NaN;
  return Number.isNaN(time) ? 0 : time;
}

function getAuditStatusRaw(audit: StatusAuditLike): string | undefined {
  return audit.data?.Status ?? audit.data?.status;
}

function getAuditJustification(audit: StatusAuditLike): string | undefined {
  const raw = audit.data?.Justificativa ?? audit.data?.justification;
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  return trimmed || undefined;
}

export function isStatusTransitionAudit(audit: StatusAuditLike): boolean {
  const status = getAuditStatusRaw(audit);
  const justification = getAuditJustification(audit);
  if (audit.action === "StatusChange") {
    return Boolean(status || justification);
  }
  return audit.action === "Update" && Boolean(status);
}

interface Transition {
  value: string;
  label: string;
  at: Date;
  justification?: string;
}

function buildTransitions(
  audits: StatusAuditLike[],
  createdAt: Date
): Transition[] {
  const sorted = [...audits]
    .filter(isStatusTransitionAudit)
    .sort((a, b) => getAuditTimestamp(a) - getAuditTimestamp(b));

  const initial = resolveStatus("BUDGET")!;
  const transitions: Transition[] = [
    { value: initial.value, label: initial.label, at: createdAt },
  ];

  for (const audit of sorted) {
    const justification = getAuditJustification(audit);
    const resolved = resolveStatus(getAuditStatusRaw(audit));
    const last = transitions[transitions.length - 1];

    if (!resolved) {
      if (justification) last.justification = justification;
      continue;
    }

    if (last.value === resolved.value) {
      if (justification) last.justification = justification;
      continue;
    }

    transitions.push({
      value: resolved.value,
      label: resolved.label,
      at: new Date(getAuditTimestamp(audit)),
      justification,
    });
  }

  return transitions;
}

function buildHistoryRows(
  transitions: Transition[],
  now: Date
): StatusHistoryRow[] {
  const occurrenceByStatus: Record<string, number> = {};

  return transitions.map((transition, index) => {
    const next = transitions[index + 1];
    const enteredAt = transition.at;
    const exitedAt = next ? next.at : null;
    const isOpenTerminal =
      !exitedAt && isTerminalTimelineStatus(transition.value);
    const end = exitedAt ?? (isOpenTerminal ? enteredAt : now);
    const durationSeconds = Math.max(
      0,
      (end.getTime() - enteredAt.getTime()) / 1000
    );
    const occurrence = (occurrenceByStatus[transition.value] ?? 0) + 1;
    occurrenceByStatus[transition.value] = occurrence;

    return {
      statusValue: transition.value,
      statusLabel: transition.label,
      enteredAt,
      exitedAt,
      enteredAtLabel: formatHistoryDateTime(enteredAt),
      exitedAtLabel: exitedAt
        ? formatHistoryDateTime(exitedAt)
        : "em andamento",
      durationSeconds,
      durationLabel: formatDuration(durationSeconds),
      occurrence,
      occurrenceLabel: formatOccurrence(occurrence),
      isReturn: occurrence > 1,
      justification: transition.justification,
    };
  });
}

function collectDurationsAndVisits(history: StatusHistoryRow[]): {
  durations: Record<string, number>;
  visits: Record<string, number>;
  labels: Record<string, string>;
} {
  const durations: Record<string, number> = {};
  const visits: Record<string, number> = {};
  const labels: Record<string, string> = {};

  for (const row of history) {
    durations[row.statusValue] =
      (durations[row.statusValue] || 0) + row.durationSeconds;
    visits[row.statusValue] = Math.max(
      visits[row.statusValue] || 0,
      row.occurrence
    );
    labels[row.statusValue] = row.statusLabel;
  }

  return { durations, visits, labels };
}

function pipelineIndex(statusValue: string, pipeline: string[]): number {
  return pipeline.indexOf(statusValue);
}

function resolveVisualState(
  statusValue: string,
  currentValue: string | undefined,
  visitCount: number,
  pipeline: string[]
): TimelineVisualState {
  if (currentValue && statusValue === currentValue) {
    if (isTerminalTimelineStatus(statusValue)) return "completed";
    return "current";
  }
  if (visitCount === 0) return "pending";

  const currentIndex = currentValue
    ? pipelineIndex(currentValue, pipeline)
    : -1;
  const stepIndex = pipelineIndex(statusValue, pipeline);

  if (currentIndex >= 0 && stepIndex > currentIndex) return "reopened";
  return "completed";
}

function buildDisplayOrder(
  visitedValues: string[],
  currentValue: string | undefined
): string[] {
  const visited = new Set(visitedValues);
  if (currentValue) visited.add(currentValue);

  const extra = Array.from(visited).filter(
    (value) => !TIMELINE_TRACK_VALUES.includes(value as never)
  );
  return [...TIMELINE_TRACK_VALUES, ...extra];
}

export function buildProjectStatusTimeline(params: {
  audits: StatusAuditLike[] | undefined | null;
  createdAt: string | Date;
  currentStatus?: string | null;
  currentJustification?: string | null;
  now?: Date;
}): ProjectStatusTimelineModel {
  const now = params.now ?? new Date();
  const createdAt = new Date(params.createdAt);
  const current = resolveStatus(params.currentStatus ?? undefined);
  const currentJustification = params.currentJustification?.trim() || undefined;
  const transitions = buildTransitions(params.audits ?? [], createdAt);
  const history = buildHistoryRows(transitions, now);
  const { durations, visits, labels } = collectDurationsAndVisits(history);

  const justifications: Record<string, string | undefined> = {};
  for (const transition of transitions) {
    if (transition.justification) {
      justifications[transition.value] = transition.justification;
    }
  }

  if (current?.value && currentJustification) {
    justifications[current.value] = currentJustification;
    const lastHistory = history[history.length - 1];
    if (lastHistory?.statusValue === current.value) {
      lastHistory.justification = currentJustification;
    }
  }

  const displayOrder = buildDisplayOrder(Object.keys(visits), current?.value);
  const pipeline = [
    ...TIMELINE_TRACK_VALUES,
    ...displayOrder.filter(
      (value) => !TIMELINE_TRACK_VALUES.includes(value as never)
    ),
  ];

  const steps: TimelineStep[] = displayOrder.map((statusValue) => {
    const resolved = resolveStatus(statusValue)!;
    const visitCount = visits[statusValue] ?? 0;
    const durationSeconds = durations[statusValue] ?? 0;
    const visualState = resolveVisualState(
      statusValue,
      current?.value,
      visitCount,
      pipeline
    );

    return {
      statusValue,
      statusLabel: labels[statusValue] ?? resolved.label,
      durationSeconds,
      durationLabel:
        visitCount > 0 || visualState === "current"
          ? formatDuration(durationSeconds)
          : "",
      visitCount,
      isRevisited: visitCount > 1,
      visualState,
      justification: justifications[statusValue],
    };
  });

  const lastHistory = history[history.length - 1];
  const totalEnd =
    lastHistory &&
    !lastHistory.exitedAt &&
    isTerminalTimelineStatus(lastHistory.statusValue)
      ? lastHistory.enteredAt
      : now;
  const totalDurationSeconds = Math.max(
    0,
    (totalEnd.getTime() - createdAt.getTime()) / 1000
  );

  return {
    steps,
    history,
    totalDurationSeconds,
    totalDurationLabel: formatDuration(totalDurationSeconds),
    createdAtLabel: formatCreatedAtShort(createdAt),
  };
}

export const TIMELINE_STATE_LABEL: Record<TimelineVisualState, string> = {
  pending: "",
  completed: "Concluído",
  current: "Atual",
  reopened: "Reaberto",
};
